import {GlossaryButton} from "components/Glossary/GlossaryButton"
import {FC} from "react"
import {Slide, Carousel} from "components/Carousel"
import beforeImageSrc from "public/images/burnout-recovery-before.png"
import afterImageSrc from "public/images/burnout-recovery-after.png"
import {Glossary} from "../Glossary"
import {Block} from "./Block"
import {CoursePageProps} from "./CoursePageProps"
import {HeadlineText} from "./HeadlineText"
import {Octopussy} from "./Octopussy"
import styles from "./course.module.css"

export const BurnoutDescription: FC<Pick<CoursePageProps, "course">> = ({course}) => {
  const [firstTerm, ...restTerms] = course.glossary

  return (
    <>
      <Block>
        <HeadlineText>
          Welcome to Burnout Recovery — personalized therapy to help you overcome
          <GlossaryButton
            className="ml-3"
            title={firstTerm.title ?? ""}
            description={firstTerm.description[0].content!.value}
          />
        </HeadlineText>
        <p>
          <b>In 12 short weeks you can</b>
        </p>
        <ul className="list-rhombus">
          <li>Decrease stress levels</li>
          <li>Reduce emotional fatigue</li>
          <li>Improve motivation</li>
          <li>Feel more optimistic</li>
          <li>Find a sense of purpose</li>
          <li>Perform at your best</li>
          <li>Elevate those around you</li>
        </ul>
      </Block>
      <Block>
        <h1 className="mt-16">How does it work?</h1>
        <p>
          Your journey starts with a thorough diagnostic assessment. It takes a few
          minutes to answer the Qs. In return, you get comprehensive results that reveal
          hidden truths about your inner psyche.
        </p>
        <p>
          Once YUNG knows more about you, then it provides daily guided therapeutic
          exercises based on your life situation, individual preferences, and your current
          mental state.
        </p>
      </Block>
      <Block>
        <h1 className="mt-16">What is a YUNG score?</h1>
        <p>Yung has identified 8 dynamic factors that characterize human development.</p>
        <p>
          Once you finish the initial assessment, we’ll present you with the YUNG Index.
          The YUNG Index measures each factor based on your responses and translates their
          values into your YUNG score.
        </p>
        <p>
          Your initial score sets a baseline to give you a snapshot of your life and
          mental state right now.
        </p>
      </Block>
      <Carousel className="mx-auto mt-12 max-w-tablet-frame">
        <Slide className={styles.slide}>
          <Octopussy image={beforeImageSrc} title="Before the course" subtitle="" />
        </Slide>
        <Slide className={styles.slide}>
          <Octopussy image={afterImageSrc} title="After the course" subtitle="" />
        </Slide>
      </Carousel>
      <Block>
        <p>
          As you progress along your journey your score will improve and you’ll see visual
          confirmation of transformation as it happens.
        </p>
      </Block>
      <Block>
        <h2 className="mt-12">Maybe I should see a therapist…</h2>
        <p>
          You can, and totally should! YUNG’s services aren’t meant to replace
          professional therapy. Our mission is to assist in your mental development with
          simple, non-intrusive guided therapy and wellness exercises.
        </p>
        <p>
          Think of YUNG as a prescription medicine. It’s not live therapy, but it’s the
          next best thing, and it’s way way way better than modern self-help apps.
        </p>
        <p>
          YUNG is developed by super-smart people and mental health professionals to bring
          you personalized exercises to help you overcome <b>burnout</b>. Our exercises
          span approaches including:
        </p>
        <div className="mt-4">
          <Glossary glossary={restTerms} />
        </div>
        <p>
          It’s all tailored based on your personal situation and daily responses. The more
          you use YUNG, the more personalized your experience becomes.
        </p>
      </Block>
      <Block>
        <h1 className="mt-16">What can I expect?</h1>
        <p>
          Our program relies on 3 pillars: diagnostics, personalization, and
          psychotherapy.
        </p>
        <p>
          <i>
            Each day YUNG AI chooses from over 1500 therapeutic exercises to create a
            bespoke guided audio therapy session for you.
          </i>
        </p>
        <p>
          <i>
            This session is the Core Practice of your Daily Plan, that our AI builds for
            you every day. Other activities include light reading, journaling and various
            exercises to help improve your YUNG score and personal life outlook.
          </i>
        </p>
        <p>
          Your Core Practice takes 15 to 25 minutes to complete, and you’ll probably
          finish all of the other Daily Plan exercises in another half hour.
        </p>
        <p>Best part?</p>
        <p>
          Thanks to certain world events, therapists are booked beyond capacity. You’re
          lucky if you can get an appointment in 2-3 months. With YUNG, there’s no wait,
          and we’ve even got mental health professionals available 24/7.
        </p>
      </Block>
      <Block>
        <h1 className="mt-16">Program Outcomes</h1>
        <p>
          Once you finish Burnout Recovery program, any feelings of burnout should
          subside. Productivity and job satisfaction should increase dramatically.
        </p>
      </Block>
      <Block>
        <p className="mt-12">
          <b>On the YUNG Index, you should see increases in:</b>
        </p>
        <ul className="list-rhombus">
          <li>Happiness</li>
          <li>Productivity</li>
          <li>Vitality</li>
          <li>Relatedness</li>
          <li>EQ</li>
          <li>Autonomy</li>
        </ul>
      </Block>
      <Block>
        <p className="mt-16">
          If you still choose to make changes, such as starting a new career, you will
          make them more mindfully and logically.
        </p>
      </Block>
      <Block>
        <h1 className="mt-12">
          In 12 weeks, you’ll feel like a completely different person!
        </h1>
        <p>Beat burnout. Reach peak performance. Start your YUNG journey today!</p>
      </Block>
    </>
  )
}
